import { configureBrowserSdk, createAppQueryClient } from '@repo/sdk'
import type { BreadcrumbFromQueryClient } from '@repo/ui/commons/query-breadcrumbs'
import type { QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import type { AppLanguageCode, TKey } from '@/lib/i18n'
import { getInitialAppLanguage } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import { initSentry } from '@/lib/sentry'
import { ErrorPage, LoadingPage, NotFoundPage } from '@/routes/__root'
import { routeTree } from './routeTree.gen'

export interface RouterContext {
	appLanguage: AppLanguageCode
	queryClient: QueryClient
}

export function getRouter() {
	const queryClient = createAppQueryClient()
	const router = createRouter({
		routeTree,
		context: {
			appLanguage: getInitialAppLanguage(),
			queryClient
		} satisfies RouterContext,
		defaultPendingMs: 0,
		defaultPreload: 'intent',
		defaultPreloadStaleTime: 0,
		scrollRestoration: true,
		defaultErrorComponent: ErrorPage,
		defaultNotFoundComponent: NotFoundPage,
		defaultPendingComponent: LoadingPage
	})

	// Client bootstrap. `StartClient` and `hydrateStart` do not expose a
	// post-hydrate hook for one-time side effects, and pre-emptively calling
	// `hydrateStart()` from `client.tsx` creates a second router instance in
	// production (no HMR promise cache there) — bootstrap would attach to a
	// router that is never mounted via `RouterProvider`. Running here gates
	// off `router.isServer` so SSR stays untouched.
	if (!router.isServer) {
		initSentry(router)
		configureBrowserSdk({
			queryClient,
			onUnauthorized: () => router.invalidate(),
			getCsrfToken: (client) =>
				client.getQueryData<{ csrfToken?: string }>(
					makeQueryKeys.auth.session()
				)?.csrfToken,
			isSessionAuthenticated: (client) =>
				client.getQueryData<{ sessionType?: string }>(
					makeQueryKeys.auth.session()
				)?.sessionType === 'authenticated'
		})
	}

	setupRouterSsrQueryIntegration({
		router,
		queryClient
	})

	return router
}

declare module '@tanstack/react-router' {
	interface Register {
		router: ReturnType<typeof getRouter>
	}

	// Ephemeral email OTP challenge (single-use token + the address it was
	// sent to) carried in history state — never the URL — from the request
	// step (otp/signup) to the verify step.
	interface HistoryState {
		emailOtpToken?: string
		email?: string
	}

	// Breadcrumb metadata for the shared @repo/ui query-breadcrumbs. Declared
	// per-app so the key is validated against this app's full TKey (the package
	// only knows the common namespace).
	interface StaticDataRouteOption {
		breadcrumb?:
			| TKey
			| { key: TKey; count?: number }
			| ((ctx: {
					params: Record<string, string>
					queryClient: QueryClient
			  }) => BreadcrumbFromQueryClient)
	}
}
